import { useState, useEffect } from 'react';
import { Avatar } from '@mui/material';
import { useRouter } from 'next/router';
import { userIsAdmin, buildJobApplicantsListPath } from 'lib/helpers';
import { Job } from 'lib/types/jobs';
import { imageAvatarUrl } from 'lib/utils/imageUtils';
import { A } from 'components/A';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { ProfileUser } from 'lib/types/users';
import { useSignUpModal } from 'components/SignUpModal';
import { Modal } from 'components/Modal';
import { ProspectForm } from 'components/ProspectForm';
import { trackClickSignupEvent } from 'lib/hooks/useEventTracking';

interface Props {
  profileUser: ProfileUser;
  job: Job;
}

export const ProspectsJobDetail = ({ profileUser, job }: Props) => {
  const router = useRouter();
  const { apply } = router.query;
  const currentUser = useCurrentUser();
  const [open, setIsOpen] = useState(false);
  const { showSignUpModal } = useSignUpModal();
  const API = useApi();

  useEffect(() => {
    if (apply && currentUser?.id) {
      setIsOpen(true);
      return;
    }

    if (apply && !currentUser?.id) {
      trackClickSignupEvent(API, 'apply-for-prospects-job');
      showSignUpModal('Please sign up or sign in to apply');
    }
  }, [apply, currentUser?.id]);

  if (!job) return null;

  const currentUserIsTeamMember = currentUser
    && currentUser.teams?.some((team) => team.id === job.teamId);

  const handleClick = () => {
    if (!currentUser) {
      trackClickSignupEvent(API, 'apply-for-prospects-job');
      showSignUpModal('Please sign up or sign in to apply');
      return;
    }
    setIsOpen(true);
  };

  return (
    <div className="max-w-screen-lg mx-auto py-8 px-4">
      <div className="flex flex-wrap sm:flex-nowrap gap-6 items-center mb-4">
        <div className="w-full sm:w-1/5">
          <A href={`/${job.account.username}`}>
            <Avatar
              sx={{ width: 100, height: 100, margin: 'auto' }}
              variant="circular"
              alt={job.account.name}
              src={imageAvatarUrl(job.account.avatarUrl, 100)}
            />
          </A>
        </div>
        <div className="w-full sm:w-4/5 text-center sm:text-left">
          <h1 className="text-2xl font-bold mb-4">
            Join our prospects list!
          </h1>
          <div className="flex mb-4">
            <h2 className="text-xl font-semibold">
              <A href={`/${job.account.username}`}>
                {job.account.name}
              </A>
            </h2>
            <div className="px-4">&bull;</div>
            <h3 className="text-lg">
              @
              {job.account.username}
            </h3>
          </div>
          <div>
            <Modal
              open={open}
              onCancel={() => setIsOpen(false)}
            >
              <ProspectForm
                jobId={job.id}
                profileUser={profileUser}
                open={open}
                onCancel={() => setIsOpen(false)}
              />
            </Modal>
            <button type="button" className="btn-primary" onClick={() => handleClick()}>Apply</button>
            {userIsAdmin(currentUser) && (
              <button type="button" className="btn-secondary ml-4" onClick={() => router.push(`/jobs/${job.id}/edit`)}>Edit</button>
            )}
            {(currentUserIsTeamMember && job.list) && (
              <button type="button" className="btn-primary-outlined ml-4" onClick={() => router.push(buildJobApplicantsListPath(job.list.name, job.list.publicId))}>
                View applicants
              </button>
            )}
          </div>
        </div>
      </div>
      {job.description && (
        <div className="border p-8 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">Description</h2>
          {job.description.split('\n').map((d) => (
            <div key={d} className="mb-4">{d}</div>
          ))}
        </div>
      )}
    </div>
  );
};
